import {CommonModule} from '@angular/common';
import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatCardModule} from '@angular/material/card';
import {MatDividerModule} from '@angular/material/divider';
import {MatIconModule} from '@angular/material/icon';
import {MatPaginator, MatPaginatorModule} from '@angular/material/paginator';
import {MatSort, MatSortModule} from '@angular/material/sort';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {UtilisateurService} from '../../../services/utilisateurs/utilisateur.service';
import {Params, Router} from '@angular/router';
import {debounceTime, distinctUntilChanged, forkJoin, merge, startWith, Subscription, tap} from 'rxjs';

import {MatSelectModule} from '@angular/material/select';
import {Utilisateur} from '../../../models/utilisateur/UtilisateurResponse';
import {FormBuilder, FormControl, FormsModule, ReactiveFormsModule, UntypedFormControl} from '@angular/forms';
import {MatInputModule} from '@angular/material/input';
import {Adb2cService} from '../../../services/adb2c/adb2c.service';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {ProfilService} from '../../../services/profil/profil.service';
import {SupprimerDialogueComponent} from './supprimer-dialogue/supprimer-dialogue.component';
import {MatDialog} from '@angular/material/dialog';
import {
  ProfilsEntitesUtilisateursService
} from '../../../services/profils-entites-utilisateurs/profils-entites-utilisateurs.service';
import {BreadcrumbComponent} from '../../breadcrumb/breadcrumb.component';

@Component({
  selector: 'app-utilisateurs',
  templateUrl: './utilisateurs.component.html',
  styleUrl: './utilisateurs.component.scss',
  standalone: true,
  imports: [
    MatTableModule, MatIconModule, MatSortModule, MatPaginatorModule, MatCardModule,
    MatDividerModule, MatSelectModule, CommonModule, ReactiveFormsModule, MatInputModule,
    MatSnackBarModule, FormsModule, MatFormFieldModule, MatAutocompleteModule, BreadcrumbComponent
  ]
})

export class UtilisateursComponent implements AfterViewInit, OnInit, OnDestroy {
  displayedColumns: string[] = ['surname', 'givenName', 'mobilePhone', 'mail', 'fonction', 'actions'];
  fonctions: string[] = [];
  dataSource = new MatTableDataSource<Utilisateur>();
  filterFunction = '';
  filterGeneral = '';
  filtersActive = false;

  filter = this._formBuilder.group({
    general_filter: new FormControl('', []),
    fonction: new UntypedFormControl('', [])
});

  subscriptions: Subscription[] = [];

  @ViewChild(MatSort) sort?: MatSort;
  @ViewChild(MatPaginator) paginator?: MatPaginator;

  constructor(
    private utilisateurService: UtilisateurService,
    private adb2cService: Adb2cService,
    private profilsEntitesUtilisateursService: ProfilsEntitesUtilisateursService,
    private router: Router,
    public snackBar: MatSnackBar,
    private _formBuilder: FormBuilder,
    private profilService: ProfilService,
    public dialog: MatDialog
  ) {}

  ngOnInit() {
    this.loadFonctions();
    this.setupFilterListeners();
  }

  ngAfterViewInit(): void {
    if (this.sort && this.paginator) {

      this.sort.active = 'creation_datetime';
      this.sort.direction = 'desc';
      this.sort.sortChange.subscribe(() => (this.paginator!.pageIndex = 0));

      merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        startWith(null),
        tap(() => this.loadUsers())
        )
      .subscribe();
    }
    else {
      this.loadUsers();
    }

    this.filter.controls.general_filter.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      tap((searchText) => {
        this.filterGeneral = searchText || '';
        this.paginator!.pageIndex = 0;
        this.loadUsers();
      })
    ).subscribe();
  }

  loadUsers() {
    if (this.paginator && this.sort) {
      const params: Params = {
        page: this.paginator.pageIndex + 1,
        limit: this.paginator.pageSize,
        libelleProfil: this.filterFunction,
        generalFilter: this.filterGeneral,
        idApplication: 2
      };

      if (this.sort.active && this.sort.direction !== '') {
        params['orderBy'] = this.sort.active;
        params['direction'] = this.sort.direction;
      }

      const userSubscription = this.utilisateurService.getUsers(params).subscribe({
        next: (users) => {
          this.dataSource.data = users.data;
          this.paginator!.length = users.total;
        },
        error: (error) => {
          console.error('Erreur lors de la récupération des utilisateurs', error);
        }
      });
      this.subscriptions.push(userSubscription);
    }
  }

  getProfilsLibelle(user: Utilisateur): string {
    return user.profils.map(p => p.libelle_profil).join(', ');
  }

  loadFonctions() {
    this.subscriptions.push(
      this.profilService.getProfils({id_application: 2}).subscribe({
        next: (fonctions) => {
          this.fonctions = fonctions.data.map(f => f.libelle_profil)
        },
        error: (error) => {
          console.log('Erreur lors de la récupération des profils', error);
        }
      })
    )
  }

  applyFilterType(value: string): void {
    this.filterFunction = value;
    this.filter.controls.fonction.setValue(value);
    this.paginator!.pageIndex = 0;
    this.loadUsers();
  }

  clearGeneralFilter() {
    this.filter.controls['general_filter'].setValue('');
    this.loadUsers();
  }

  resetFilters() {
    this.filter.controls['fonction'].setValue('');
    this.filterFunction = '';
    this.filter.controls['general_filter'].setValue('');
    this.filterGeneral = '';
    this.loadUsers();
  }

  setupFilterListeners() {
    this.filter.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      const generalFilterActive = this.filter.controls['general_filter'].value !== '';
      const fonctionFilterActive = this.filter.controls['fonction'].value !== '';
      this.filtersActive = generalFilterActive || fonctionFilterActive;
    });
  }

  redirectToCreateUser() {
    this.router.navigate(['administration/utilisateurs/creer-utilisateur']);
  }

  redirectToEditUser(userId: string) {
    this.router.navigate(['administration/utilisateurs/modifier-utilisateur', userId]);
  }

  openDeleteDialog(id: string): void {
    const dialogRef = this.dialog.open(SupprimerDialogueComponent, {
      width: '500px',
      height: '180px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
         this.deleteUser(id);
      }
    });
  }

  deleteUser(id: string) {
    this.subscriptions.push(
      this.utilisateurService.getUserById(id).subscribe({
        next: (response) => {
          const user = response.data;
          const hasApp1Profile = user.profils.some(profil => profil.id_application === 1);

          if (hasApp1Profile) {
            const profilsToDelete = user.profils.filter(profil => profil.id_application === 2);
            console.log(profilsToDelete);

            profilsToDelete.map(profil => this.profilsEntitesUtilisateursService.getProfilEntityUser({users_id: id, profil_id: profil.id}).subscribe({
            next: (response) => {
              const profilsEntitesUtilisateurs = response.data[0];
              this.profilsEntitesUtilisateursService.deleteProfilEntityUser(profilsEntitesUtilisateurs.id).subscribe({
                next:() => {
                  this.snackBar.open('Utilisateur supprimé avec succès !', 'Fermer', {
                    verticalPosition: 'top',
                    duration: 3000,
                    panelClass: ['snackbar-success']
                  });
                }
              })
              this.loadUsers();
            },
            error: (error) => {
              console.error('Erreur lors de la suppression de l’utilisateur', error);
              this.snackBar.open('Erreur lors de la suppression de l’utilisateur !', 'Fermer', {
                verticalPosition: 'top',
                duration: 3000,
                panelClass: ['snackbar-error']
              });
            }
            }));


          } else {
            let deletions = [];

            if (user.id) {
              deletions.push(this.utilisateurService.deleteUser(user.id.toString()));
            }

            if (user.id_adb2c) {
              deletions.push(this.adb2cService.deleteAdb2c(user.id_adb2c));
            }

            forkJoin(deletions).subscribe({
              next: () => {
                this.snackBar.open('Utilisateur supprimé avec succès !', 'Fermer', {
                  verticalPosition: 'top',
                  duration: 5000,
                  panelClass: ['snackbar-success']
                });
                this.loadUsers();
              },
              error: (error) => {
                console.error('Erreur lors de la suppression de l’utilisateur', error);
                this.snackBar.open('Erreur lors de la suppression de l’utilisateur !', 'Fermer', {
                  verticalPosition: 'top',
                  duration: 5000,
                  panelClass: ['snackbar-error']
                });
              }
            });
          }
        },
        error: (error) => {
          console.error('Erreur lors de la récupération de l’utilisateur', error);
          this.snackBar.open('Erreur lors de la suppression de l’utilisateur !', 'Fermer', {
            verticalPosition: 'top',
            duration: 5000,
            panelClass: ['snackbar-error']
          });
        }
      })
    )
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(x => x.unsubscribe());
  }
}
